<template>
  <div class="card card-body">

    <!-- Download -->
    <button class="btn btn-outline-secondary mb-2" @click="downloadSample">
      Download Sample Excel
    </button>

    <!-- Upload -->
    <input type="file" accept=".xlsx,.xls" class="form-control mb-3" @change="handleFile" />

    <!-- Search -->
    <input v-if="rows.length" class="form-control mb-2" placeholder="Search..." v-model="search" />

    <!-- Progress -->
    <div v-if="isImporting" class="mb-3">
      <div class="progress">
        <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: progress + '%' }">
          {{ progress }}%
        </div>
      </div>
      <small class="text-muted">
        Imported {{ processedRows }} of {{ totalRows }} rows
      </small>
    </div>

    <!-- Table -->
    <div class="table-responsive" style="max-height:420px; overflow-y:auto">
      <table class="table table-sm table-bordered">
        <thead class="table-light">
          <tr>
            <th>#</th>
            <th>Account Name</th>
            <th>Account Type</th>
            <th>Account Category</th>
            <th>Opening Balance</th>
            <th>Monthly Savings</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(r, i) in filteredRows" :key="r._rowIndex" :class="rowClass(r)">
            <td>{{ i + 1 }}</td>

            <td>
              <input class="form-control form-control-sm" v-model="r.name" @input="validateRow(r)" />
            </td>

            <td>
              <select class="form-select form-select-sm" v-model="r.type" @change="validateRow(r)">
                <option value="">Select</option>
                <option v-for="t in ACCOUNT_TYPES" :key="t" :value="t">
                  {{ t }}
                </option>
              </select>
            </td>

            <td>
              <select class="form-select form-select-sm" v-model="r.accountCategory" @change="validateRow(r)">
                <option value="">Select</option>
                <option v-for="c in ACCOUNT_CATEGORIES" :key="c" :value="c">
                  {{ c }}
                </option>
              </select>
            </td>

            <td>
              <input type="number" class="form-control form-control-sm" v-model.number="r.openingBalance"
                @input="validateRow(r)" />
            </td>

            <td>
              <input type="number" class="form-control form-control-sm" v-model.number="r.monthlySavings"
                @input="validateRow(r)" />
            </td>

            <td>
              <span v-if="r._status === 'SUCCESS'" class="text-success">
                ✔ {{ r._remark }}
              </span>
              <span v-else-if="r._status === 'FAILED'" class="text-danger">
                ✖ {{ r._remark || 'Invalid data' }}
              </span>
              <span v-else class="text-muted">Pending</span>
            </td>

            <td>
              <button class="btn btn-sm btn-danger" @click="deleteRow(i)">
                ✖
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Undo -->
    <button v-if="deletedRows.length" class="btn btn-warning mt-2" @click="undoDelete">
      Undo Delete
    </button>

    <!-- Submit -->
    <button class="btn btn-success mt-3" @click="submitRows" :disabled="isImporting">
      Submit Valid Rows
    </button>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import accountApi from '@/api/accountApi'

export default {
  name: 'AccountOpeningImport',

  data() {
    return {
      rows: [],
      deletedRows: [],
      search: '',
      isImporting: false,
      progress: 0,
      processedRows: 0,
      totalRows: 0,

      ACCOUNT_TYPES: ['ASSET', 'LIABILITY'],
      ACCOUNT_CATEGORIES: ['CASH', 'BANK', 'INVESTMENT', 'SAVING']
    }
  },

  computed: {
    filteredRows() {
      if (!this.search) return this.rows

      const q = this.search.toLowerCase()
      return this.rows.filter(r =>
        [
          r.name,
          r.type,
          r.accountCategory,
          r.openingBalance,
          r.monthlySavings
        ]
          .join(' ')
          .toLowerCase()
          .includes(q)
      )
    }
  },

  methods: {
    /* ===============================
     * DOWNLOAD SAMPLE
     * =============================== */
    async downloadSample() {
      const res = await accountApi.getClientAccounts()

      const wb = new ExcelJS.Workbook()
      const sheet = wb.addWorksheet('ClientOpening')

      sheet.columns = [
        { header: 'Client ID', key: 'clientId', hidden: true },
        { header: 'Account ID', key: 'accountId', hidden: true },
        { header: 'Account Name', key: 'name' },
        { header: 'Client ID Number', key: 'clientIdNumber'},
        { header: 'Account Type', key: 'type' },
        { header: 'Account Category', key: 'accountCategory' },
        { header: 'Opening Balance', key: 'openingBalance' },
        { header: 'Monthly Savings Amount', key: 'monthlySavings' }
      ]

      res.data.forEach(r => {
        sheet.addRow({
          clientId: r.clientId,
          accountId: r.accountId,
          clientIdNumber: r.clientIdNumber,
          name: r.name,
          type: r.type,
          accountCategory: r.accountCategory,
          openingBalance: r.balance ?? 0,
          monthlySavings: r.monthlySavings ?? 0
        })
      })

      sheet.getRow(1).font = { bold: true }
      sheet.views = [{ state: 'frozen', ySplit: 1 }]
      await sheet.protect('lock')

      sheet.columns.forEach(col => {
        col.protection = { locked: true }
      })

        ;['openingBalance', 'monthlySavings'].forEach(key => {
          sheet.getColumn(key).protection = { locked: false }
        })

      const buffer = await wb.xlsx.writeBuffer()
      saveAs(
        new Blob([buffer], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        'client_account_opening.xlsx'
      )
    },

    /* ===============================
     * IMPORT FILE
     * =============================== */
    handleFile(e) {
      const file = e.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = ev => {
        const wb = XLSX.read(ev.target.result, { type: 'array' })
        const sheet = wb.Sheets[wb.SheetNames[0]]
        const data = XLSX.utils.sheet_to_json(sheet, { defval: '' })

        this.rows = data.map((r, idx) => ({
          clientId: Number(r['Client ID']),
          accountId: Number(r['Account ID']),
          name: r['Account Name'],
          type: r['Account Type'],
          accountCategory: r['Account Category'],
          openingBalance: Number(r['Opening Balance']) || 0,
          monthlySavings: Number(r['Monthly Savings Amount']) || 0,
          _rowIndex: idx,
          _errors: {},
          _status: 'PENDING',
          _remark: ''
        }))

        this.rows.forEach(this.validateRow)
      }

      reader.readAsArrayBuffer(file)
    },

    validateRow(r) {
      r._errors = {}

      if (!r.clientId) r._errors.clientId = true
      if (!r.accountId) r._errors.accountId = true
      if (isNaN(r.openingBalance)) r._errors.openingBalance = true
      if (isNaN(r.monthlySavings)) r._errors.monthlySavings = true

      r._status = Object.keys(r._errors).length ? 'FAILED' : 'PENDING'
    },

    rowClass(r) {
      if (r._status === 'SUCCESS') return 'table-success'
      if (r._status === 'FAILED') return 'table-danger'
      return ''
    },

    deleteRow(i) {
      this.deletedRows.push(this.rows[i])
      this.rows.splice(i, 1)
    },

    undoDelete() {
      this.rows.push(...this.deletedRows)
      this.deletedRows = []
    },

    /* ===============================
     * SUBMIT
     * =============================== */
    async submitRows() {
      const validRows = this.rows.filter(
        r => Object.keys(r._errors).length === 0
      )

      if (!validRows.length) {
        alert('No valid rows to submit')
        return
      }

      this.isImporting = true
      this.totalRows = validRows.length
      this.processedRows = 0
      this.progress = 0

      for (let i = 0; i < validRows.length; i += 10) {
        const chunk = validRows.slice(i, i + 10)

        try {
          const res = await accountApi.importBatch(
            chunk.map(r => ({
              index: r._rowIndex,
              clientId: r.clientId,
              accountId: r.accountId,
              openingBalance: r.openingBalance,
              monthlySavings: r.monthlySavings
            }))
          )

          res.data.forEach(x => {
            const row = this.rows.find(r => r._rowIndex === x.index)
            if (row) {
              row._status = x.status
              row._remark = x.message
            }
          })
        } catch {
          chunk.forEach(r => {
            r._status = 'FAILED'
            r._remark = 'System error'
          })
        }

        this.processedRows += chunk.length
        this.progress = Math.round(
          (this.processedRows / this.totalRows) * 100
        )
      }

      this.isImporting = false
    }
  }
}
</script>
